/**
 * Client-side mirror of the limits enforced by the server's zod schemas in
 * packages/server/src/routes/staff.ts (`CreateBody`, `ResetPinBody`,
 * `ResetPasswordBody`). This admin worktree does not own `packages/server`
 * or `packages/shared`, so these numbers cannot literally be imported from
 * one shared module the way money helpers are — see
 * docs/decisions/2026-07-31-staff-limits-duplication.md for why, and what a
 * follow-up merge should do about it (move these into `@equinox/shared`).
 *
 * Until then: this is the ONE place in the admin package these numbers are
 * written down. Every form that validates a staff password, PIN or discount
 * ceiling imports from here — nowhere else declares its own copy of "12" or
 * "0 to 100". `staffLimits.test.ts` pins these values against the literal
 * text of the server route file, so a server-side change that isn't mirrored
 * here fails CI instead of shipping a second F1-shaped bug.
 */

/** Mirrors `CreateBody`/`ResetPasswordBody`'s `z.string().min(12)`. */
export const STAFF_PASSWORD_MIN_LENGTH = 12;

/** Mirrors `PIN_PATTERN = /^\d{4,6}$/` shared by `CreateBody` and `ResetPinBody`. */
export const STAFF_PIN_PATTERN = /^\d{4,6}$/;
export const STAFF_PIN_MIN_DIGITS = 4;
export const STAFF_PIN_MAX_DIGITS = 6;

/** Mirrors `CreateBody`/`UpdateBody`'s `z.number().min(0).max(100)`. */
export const STAFF_DISCOUNT_CEILING_MIN = 0;
export const STAFF_DISCOUNT_CEILING_MAX = 100;

export function isValidStaffPassword(password: string): boolean {
  return password.length >= STAFF_PASSWORD_MIN_LENGTH;
}

export function isValidStaffPin(pin: string): boolean {
  return STAFF_PIN_PATTERN.test(pin);
}

export function isValidDiscountCeiling(pct: number): boolean {
  return Number.isFinite(pct) && pct >= STAFF_DISCOUNT_CEILING_MIN && pct <= STAFF_DISCOUNT_CEILING_MAX;
}
